import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, Clock, Users, Target, 
  Mail, Phone, MapPin, Linkedin, Building2, Calendar,
  ChevronDown, ChevronUp, Loader2, Check, AlertCircle, Ticket, Copy
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CafetrixSchedule = () => {
  const [formData, setFormData] = useState({
    // Contact Information
    fullName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    // Organization Details
    organizationType: '',
    employeeCount: '',
    location: '',
    cafeteriaCount: '',
    // Current Setup
    currentVendor: '',
    annualBudget: '',
    dailyMealCount: '',
    // Pain Points
    painPoints: [],
    otherPainPoint: '',
    // Specific Concerns
    specificConcerns: '',
    // Priority
    priorityArea: '',
    // Meeting Preferences
    meetingFormat: '',
    availableFrom: '',
    preferredTimeSlots: [],
    // Background
    companyBackground: '',
    // Referral
    referralSource: '',
    otherReferral: '',
    // Consent
    privacyConsent: false,
    marketingConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [copied, setCopied] = useState(false);

  const jobTitles = [
    'Facilities Manager',
    'HR Manager',
    'Operations Director',
    'Campus Head',
    'Catering Manager',
    'Admin Manager',
    'CFO/Finance Head',
    'Other'
  ];

  const organizationTypes = [
    'IT/ITeS Company',
    'Financial Services/Banking',
    'Manufacturing',
    'Government',
    'Educational Institution',
    'IT Park/SEZ',
    'Catering Company',
    'Healthcare/Hospital',
    'Retail/Hospitality',
    'Other'
  ];

  const employeeCounts = [
    '100-500',
    '500-1000',
    '1000-2500',
    '2500-5000',
    '5000+'
  ];

  const locations = [
    'Bengaluru',
    'Hyderabad',
    'Pune',
    'Mumbai',
    'Delhi-NCR',
    'Gurgaon',
    'Chennai',
    'Kolkata',
    'Ahmedabad',
    'Other'
  ];

  const budgetRanges = [
    'Below ₹50L',
    '₹50L-₹1Cr',
    '₹1-5Cr',
    '₹5-10Cr',
    '₹10Cr+'
  ];

  const painPointOptions = [
    'Long queues and service delays',
    'Menu doesn\'t match employee preferences/diversity',
    'Rising costs without ROI visibility',
    'Food waste and sustainability concerns',
    'Poor employee satisfaction with cafeteria',
    'Kitchen inefficiency and equipment issues',
    'Manpower/staffing challenges',
    'Selecting the right food service provider',
    'Unclear/unfair commercial terms with vendor',
    'ESG and waste reporting requirements',
    'Water management and conservation'
  ];

  const priorityAreas = [
    'Operational Efficiency & Cost Reduction',
    'Employee Satisfaction & Experience',
    'Sustainability & Waste Management',
    'Provider Selection & Commercial Terms',
    'Menu & Kitchen Redesign',
    'Manpower Optimization',
    'All of the Above',
    'Not Sure (let us guide you)'
  ];

  const meetingFormats = [
    'In-person visit to our campus/facility',
    'Virtual meeting (Zoom/Teams call)',
    'Phone consultation',
    'Flexible/As convenient'
  ];

  const timeSlots = [
    '9:00-11:00 AM',
    '11:00-1:00 PM',
    '2:00-4:00 PM',
    '4:00-6:00 PM',
    'Flexible'
  ];

  const referralSources = [
    'LinkedIn',
    'Google Search',
    'Referral from colleague/contact',
    'Industry event',
    'Website',
    'Direct email/outreach',
    'Other'
  ];

  const faqs = [
    {
      question: 'What happens after I submit?',
      answer: 'Within 24-48 business hours, Sandeep or a member of our team will reach out via email or phone to confirm your meeting time and gather any additional details needed.'
    },
    {
      question: 'Is this consultation free?',
      answer: 'Yes! The 30-minute diagnostic consultation is complimentary. We\'ll assess your situation and provide 3 immediate wins at no cost or obligation.'
    },
    {
      question: 'What should I prepare before the call?',
      answer: 'Having basic information ready helps: current vendor name, approximate daily meal count, annual budget range, and your top 2-3 pain points. But don\'t worry if you don\'t have all details—we\'ll guide the conversation.'
    },
    {
      question: 'Can multiple team members join?',
      answer: 'Absolutely! We encourage having key stakeholders (Facilities, HR, Finance) join the call for a more comprehensive discussion.'
    },
    {
      question: 'How long does a full engagement take?',
      answer: 'A typical engagement runs 6-12 weeks from initial assessment to final recommendations. Implementation support can extend 2-3 months additionally.'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxArrayChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.company.trim()) newErrors.company = 'Company name is required';
    if (!formData.jobTitle) newErrors.jobTitle = 'Job title is required';
    if (!formData.organizationType) newErrors.organizationType = 'Organization type is required';
    if (!formData.employeeCount) newErrors.employeeCount = 'Employee count is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.privacyConsent) newErrors.privacyConsent = 'Please accept the privacy policy';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.error-field');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cafetrix/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        setSubmissionResult(result);
        setIsSubmitted(true);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Generate a mock ticket for demo
      setSubmissionResult({
        ticket_number: `CLX-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
        fullName: formData.fullName,
        email: formData.email,
        company: formData.company
      });
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyTicketNumber = () => {
    if (submissionResult?.ticket_number) {
      navigator.clipboard.writeText(submissionResult.ticket_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isSubmitted) {
    return (
      <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
        <Header />
        <main style={{ paddingTop: '180px' }}>
          <section style={{ padding: '100px 7.6923%' }}>
            <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #00D4FF 0%, #00a8cc 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 40px'
              }}>
                <Check size={50} color="#ffffff" />
              </div>
              <h1 className="display-medium" style={{ marginBottom: '20px' }}>
                Thank You! Your Diagnostic Request Has Been <span style={{ color: '#00D4FF' }}>Submitted</span>
              </h1>
              
              {/* Ticket Number Display */}
              <div style={{
                padding: '30px',
                background: 'rgba(0, 212, 255, 0.1)',
                border: '2px solid #00D4FF',
                marginBottom: '30px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px' }}>
                  <Ticket size={24} color="#00D4FF" />
                  <span className="body-medium" style={{ color: 'var(--text-muted)' }}>Your Ticket Number</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                  <span style={{ 
                    fontSize: '32px', 
                    fontWeight: '700', 
                    color: '#00D4FF',
                    letterSpacing: '2px'
                  }}>
                    {submissionResult?.ticket_number || 'CLX-XXXX-XXXX'}
                  </span>
                  <button
                    onClick={copyTicketNumber}
                    style={{
                      padding: '8px 12px',
                      background: copied ? '#27ae60' : 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid var(--border-subtle)',
                      color: copied ? '#ffffff' : 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      transition: 'all 0.3s'
                    }}
                  >
                    <Copy size={14} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-muted)' }}>
                  Save this ticket number to track your request status
                </p>
              </div>

              <p className="body-large" style={{ marginBottom: '30px', color: 'var(--text-muted)' }}>
                We've received your inquiry and will reach out to schedule your 30-minute consultation within 24-48 hours.
              </p>
              <p className="body-medium" style={{ marginBottom: '40px', color: 'var(--text-muted)' }}>
                In the meantime, check your email ({formData.email}) for a copy of your submission.
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/cafetrix-labs" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: '#00D4FF',
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  transition: 'all 0.3s'
                }}>
                  Return to CafetriX Labs
                </Link>
                <Link to="/" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  border: '2px solid var(--border-subtle)',
                  transition: 'all 0.3s'
                }}>
                  Back to Home
                </Link>
              </div>

              {/* What's Next */}
              <div style={{
                marginTop: '60px',
                padding: '40px',
                background: 'rgba(0, 212, 255, 0.05)',
                border: '1px solid #00D4FF',
                textAlign: 'left'
              }}>
                <h3 className="heading-2" style={{ marginBottom: '24px' }}>What Happens Next?</h3>
                <div style={{ display: 'grid', gap: '20px' }}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#00D4FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: '600',
                      flexShrink: 0
                    }}>1</div>
                    <div>
                      <p className="body-medium"><strong>Review</strong> — Our team reviews your submission and prepares for the consultation</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#00D4FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: '600',
                      flexShrink: 0
                    }}>2</div>
                    <div>
                      <p className="body-medium"><strong>Schedule</strong> — We'll reach out within 24-48 hours to confirm your meeting time</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#00D4FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: '600',
                      flexShrink: 0
                    }}>3</div>
                    <div>
                      <p className="body-medium"><strong>Consult</strong> — 30-minute diagnostic call with actionable insights and 3 immediate wins</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 100%)',
          padding: '60px 7.6923%',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <Link to="/cafetrix-labs" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#00D4FF',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to CafetriX Labs
            </Link>

            <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_vdmbusiness/artifacts/v6hrcwjd_ChatGPT%20Image%20Jan%201%2C%202026%2C%2009_07_53%20PM.png" 
                alt="CafetriX Labs" 
                style={{ height: '60px', objectFit: 'contain', marginBottom: '20px' }}
              />
              <h1 style={{ 
                fontSize: '36px', 
                fontWeight: '700', 
                color: '#ffffff',
                marginBottom: '16px'
              }}>
                Schedule Your <span style={{ color: '#00D4FF' }}>30-Minute Diagnostic</span>
              </h1>
              <p style={{ 
                fontSize: '18px', 
                color: 'rgba(255,255,255,0.8)',
                lineHeight: '1.6'
              }}>
                Tell us about your cafeteria challenges. We'll review your setup and share three immediate wins.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section style={{ padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 400px',
              gap: '60px',
              alignItems: 'start'
            }} className="form-layout">
              {/* Form Section */}
              <div>
                {/* Introduction */}
                <div style={{
                  padding: '30px',
                  background: 'rgba(0, 212, 255, 0.05)',
                  borderLeft: '4px solid #00D4FF',
                  marginBottom: '50px'
                }}>
                  <p className="body-medium" style={{ marginBottom: '16px' }}>
                    Your cafeteria is more than just a cost center—it's a <strong>strategic asset</strong> that impacts employee satisfaction, retention, and operational efficiency.
                  </p>
                  <p className="body-medium" style={{ marginBottom: '16px', color: 'var(--text-muted)' }}>
                    Schedule a complimentary 30-minute diagnostic consultation with our team. We'll assess your current situation, identify pain points, and provide actionable recommendations tailored to your organization.
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                    Fill out the form below. Sandeep or a member of the CafetriX Labs team will reach out within 24-48 hours to confirm your meeting time.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Section A: Contact Information */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', color: '#00D4FF' }}>
                      Contact Information
                    </h3>
                    <div style={{ display: 'grid', gap: '24px' }}>
                      <div className={errors.fullName ? 'error-field' : ''}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                          Full Name <span style={{ color: '#FF6B6B' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: errors.fullName ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                            color: 'var(--text-primary)',
                            fontSize: '16px',
                            outline: 'none'
                          }}
                        />
                        {errors.fullName && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.fullName}</p>}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }} className="form-row">
                        <div className={errors.email ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Email Address <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your.email@company.com"
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.email ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                          {errors.email && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.email}</p>}
                        </div>

                        <div className={errors.phone ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Phone Number <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+91 98765 43210"
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.phone ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                          {errors.phone && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.phone}</p>}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }} className="form-row">
                        <div className={errors.company ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Company/Organization <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            placeholder="Company name"
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.company ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                          {errors.company && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.company}</p>}
                        </div>

                        <div className={errors.jobTitle ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Job Title/Role <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <select
                            name="jobTitle"
                            value={formData.jobTitle}
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.jobTitle ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: formData.jobTitle ? 'var(--text-primary)' : 'var(--text-muted)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select your role</option>
                            {jobTitles.map(title => (
                              <option key={title} value={title}>{title}</option>
                            ))}
                          </select>
                          {errors.jobTitle && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.jobTitle}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section B: Organization Details */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', color: '#00D4FF' }}>
                      Organization Details
                    </h3>
                    <div style={{ display: 'grid', gap: '24px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }} className="form-row">
                        <div className={errors.organizationType ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Organization Type <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <select
                            name="organizationType"
                            value={formData.organizationType}
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.organizationType ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: formData.organizationType ? 'var(--text-primary)' : 'var(--text-muted)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select type</option>
                            {organizationTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                          {errors.organizationType && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.organizationType}</p>}
                        </div>

                        <div className={errors.employeeCount ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Number of Employees <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <select
                            name="employeeCount"
                            value={formData.employeeCount}
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.employeeCount ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: formData.employeeCount ? 'var(--text-primary)' : 'var(--text-muted)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select range</option>
                            {employeeCounts.map(count => (
                              <option key={count} value={count}>{count}</option>
                            ))}
                          </select>
                          {errors.employeeCount && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.employeeCount}</p>}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }} className="form-row">
                        <div className={errors.location ? 'error-field' : ''}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Location/City <span style={{ color: '#FF6B6B' }}>*</span>
                          </label>
                          <select
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: errors.location ? '2px solid #FF6B6B' : '1px solid var(--border-subtle)',
                              color: formData.location ? 'var(--text-primary)' : 'var(--text-muted)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select city</option>
                            {locations.map(loc => (
                              <option key={loc} value={loc}>{loc}</option>
                            ))}
                          </select>
                          {errors.location && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px' }}>{errors.location}</p>}
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Number of Cafeterias
                          </label>
                          <input
                            type="text"
                            name="cafeteriaCount"
                            value={formData.cafeteriaCount}
                            onChange={handleInputChange}
                            placeholder="e.g., 2"
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section C: Current Setup */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', color: '#00D4FF' }}>
                      Current Cafeteria Setup
                    </h3>
                    <div style={{ display: 'grid', gap: '24px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }} className="form-row-3">
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Current Vendor
                          </label>
                          <input
                            type="text"
                            name="currentVendor"
                            value={formData.currentVendor}
                            onChange={handleInputChange}
                            placeholder="Vendor name"
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Annual Budget (approx)
                          </label>
                          <select
                            name="annualBudget"
                            value={formData.annualBudget}
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid var(--border-subtle)',
                              color: formData.annualBudget ? 'var(--text-primary)' : 'var(--text-muted)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select range</option>
                            {budgetRanges.map(range => (
                              <option key={range} value={range}>{range}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Avg Daily Meals
                          </label>
                          <input
                            type="text"
                            name="dailyMealCount"
                            value={formData.dailyMealCount}
                            onChange={handleInputChange}
                            placeholder="e.g., 1500"
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section D: Pain Points */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '8px', color: '#00D4FF' }}>
                      Key Pain Points & Challenges
                    </h3>
                    <p className="body-small" style={{ marginBottom: '24px', color: 'var(--text-muted)' }}>
                      Select all that apply
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }} className="checkbox-grid">
                      {painPointOptions.map(option => (
                        <label key={option} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '14px',
                          background: formData.painPoints.includes(option) ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                          border: formData.painPoints.includes(option) ? '1px solid #00D4FF' : '1px solid var(--border-subtle)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.painPoints.includes(option)}
                            onChange={() => handleCheckboxArrayChange('painPoints', option)}
                            style={{ marginTop: '2px' }}
                          />
                          <span className="body-small">{option}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: '16px' }}>
                      <input
                        type="text"
                        name="otherPainPoint"
                        value={formData.otherPainPoint}
                        onChange={handleInputChange}
                        placeholder="Other challenges (please specify)"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-primary)',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  {/* Section E: Specific Concerns */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '8px', color: '#00D4FF' }}>
                      Specific Concerns & Questions
                    </h3>
                    <p className="body-small" style={{ marginBottom: '24px', color: 'var(--text-muted)' }}>
                      Tell us more about your challenges or what you'd like to achieve
                    </p>
                    <textarea
                      name="specificConcerns"
                      value={formData.specificConcerns}
                      onChange={handleInputChange}
                      placeholder="E.g., We're struggling with long lunch-hour queues and want to improve throughput. Also interested in sustainable waste management solutions..."
                      rows={5}
                      maxLength={1000}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                    <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-muted)', textAlign: 'right' }}>
                      {formData.specificConcerns.length}/1000 characters
                    </p>
                  </div>

                  {/* Section F: Priority */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', color: '#00D4FF' }}>
                      Priority Area
                    </h3>
                    <select
                      name="priorityArea"
                      value={formData.priorityArea}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: formData.priorityArea ? 'var(--text-primary)' : 'var(--text-muted)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select your top priority</option>
                      {priorityAreas.map(area => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>

                  {/* Section G: Meeting Preferences */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', color: '#00D4FF' }}>
                      Meeting Preferences
                    </h3>
                    <div style={{ display: 'grid', gap: '24px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                          Preferred Meeting Format
                        </label>
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {meetingFormats.map(format => (
                            <label key={format} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '14px',
                              background: formData.meetingFormat === format ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                              border: formData.meetingFormat === format ? '1px solid #00D4FF' : '1px solid var(--border-subtle)',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}>
                              <input
                                type="radio"
                                name="meetingFormat"
                                value={format}
                                checked={formData.meetingFormat === format}
                                onChange={handleInputChange}
                              />
                              <span className="body-medium">{format}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }} className="form-row">
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            Available From
                          </label>
                          <input
                            type="date"
                            name="availableFrom"
                            value={formData.availableFrom}
                            onChange={handleInputChange}
                            style={{
                              width: '100%',
                              padding: '14px 16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid var(--border-subtle)',
                              color: 'var(--text-primary)',
                              fontSize: '16px',
                              outline: 'none'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                            Preferred Time Slots
                          </label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {timeSlots.map(slot => (
                              <label key={slot} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 12px',
                                background: formData.preferredTimeSlots.includes(slot) ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                                border: formData.preferredTimeSlots.includes(slot) ? '1px solid #00D4FF' : '1px solid var(--border-subtle)',
                                cursor: 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={formData.preferredTimeSlots.includes(slot)}
                                  onChange={() => handleCheckboxArrayChange('preferredTimeSlots', slot)}
                                  style={{ display: 'none' }}
                                />
                                {slot}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section I: Company Background */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '8px', color: '#00D4FF' }}>
                      Company Background
                    </h3>
                    <p className="body-small" style={{ marginBottom: '24px', color: 'var(--text-muted)' }}>
                      Tell us about your organization (optional)
                    </p>
                    <textarea
                      name="companyBackground"
                      value={formData.companyBackground}
                      onChange={handleInputChange}
                      placeholder="Brief description of your organization, industry, unique challenges..."
                      rows={3}
                      maxLength={500}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Section J: Referral */}
                  <div style={{ marginBottom: '50px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', color: '#00D4FF' }}>
                      How Did You Hear About Us?
                    </h3>
                    <select
                      name="referralSource"
                      value={formData.referralSource}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: formData.referralSource ? 'var(--text-primary)' : 'var(--text-muted)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select one</option>
                      {referralSources.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                    {formData.referralSource === 'Other' && (
                      <input
                        type="text"
                        name="otherReferral"
                        value={formData.otherReferral}
                        onChange={handleInputChange}
                        placeholder="Please specify"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          marginTop: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid var(--border-subtle)',
                          color: 'var(--text-primary)',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                      />
                    )}
                  </div>

                  {/* Section K: Consent */}
                  <div style={{ marginBottom: '50px' }}>
                    <div className={errors.privacyConsent ? 'error-field' : ''} style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          name="privacyConsent"
                          checked={formData.privacyConsent}
                          onChange={handleInputChange}
                          style={{ marginTop: '4px' }}
                        />
                        <span className="body-small">
                          I agree to share this information with CafetriX Labs and receive follow-up communications at the contact details provided <span style={{ color: '#FF6B6B' }}>*</span>
                        </span>
                      </label>
                      {errors.privacyConsent && <p style={{ color: '#FF6B6B', fontSize: '13px', marginTop: '6px', marginLeft: '28px' }}>{errors.privacyConsent}</p>}
                    </div>
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        name="marketingConsent"
                        checked={formData.marketingConsent}
                        onChange={handleInputChange}
                        style={{ marginTop: '4px' }}
                      />
                      <span className="body-small">
                        I'd like to receive occasional updates, whitepapers, and industry insights from CafetriX Labs
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <div style={{ textAlign: 'center' }}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        padding: '18px 48px',
                        background: isSubmitting ? '#666' : '#00D4FF',
                        color: '#ffffff',
                        fontWeight: '700',
                        fontSize: '18px',
                        border: 'none',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s',
                        minWidth: '300px'
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          Scheduling...
                        </>
                      ) : (
                        <>
                          Schedule My Diagnostic
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>
                    <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-muted)' }}>
                      By clicking 'Schedule My Diagnostic', your inquiry will be sent to sandeep@vdmbi.org
                    </p>
                    <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                      We'll respond within 24-48 business hours to confirm your meeting
                    </p>
                  </div>
                </form>
              </div>

              {/* Side Panel */}
              <div className="side-panel" style={{ position: 'sticky', top: '200px' }}>
                {/* What to Expect */}
                <div style={{
                  padding: '30px',
                  background: 'rgba(0, 212, 255, 0.05)',
                  border: '1px solid #00D4FF',
                  marginBottom: '30px'
                }}>
                  <h3 className="heading-2" style={{ marginBottom: '20px', color: '#00D4FF' }}>
                    What to Expect
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {[
                      '30-minute consultation with an experienced consultant',
                      'Assessment of your current cafeteria setup',
                      'Identification of 3 immediate wins',
                      'Custom recommendations roadmap',
                      'No commitment—purely advisory'
                    ].map((item, index) => (
                      <li key={index} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '14px'
                      }}>
                        <CheckCircle2 size={18} color="#00D4FF" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <span className="body-small">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Why CafetriX Labs */}
                <div style={{
                  padding: '30px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  marginBottom: '30px'
                }}>
                  <h3 className="heading-2" style={{ marginBottom: '20px' }}>
                    Why CafetriX Labs
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {[
                      '28+ years of hands-on cafeteria management',
                      'Experience at Cisco, Oracle, Google, Boeing',
                      'Integrated solutions (audit, design, menu, waste)',
                      'Founder-led expertise',
                      'Proven ROI-focused recommendations'
                    ].map((item, index) => (
                      <li key={index} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '14px'
                      }}>
                        <Check size={18} color="var(--brand-primary)" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <span className="body-small">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Direct Contact */}
                <div style={{
                  padding: '30px',
                  background: 'rgba(255, 107, 107, 0.05)',
                  border: '1px solid rgba(255, 107, 107, 0.3)'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '20px' }}>
                    Prefer to Reach Out Directly?
                  </h3>
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <a href="mailto:sandeep@vdmbi.org" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'var(--text-primary)',
                      textDecoration: 'none'
                    }}>
                      <Mail size={20} color="#FF6B6B" />
                      <span className="body-small">sandeep@vdmbi.org</span>
                    </a>
                    <a href="tel:+918050518788" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'var(--text-primary)',
                      textDecoration: 'none'
                    }}>
                      <Phone size={20} color="#FF6B6B" />
                      <span className="body-small">+91 80505 18788</span>
                    </a>
                    <a href="https://www.linkedin.com/in/sandeep31051976" target="_blank" rel="noopener noreferrer" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: 'var(--text-primary)',
                      textDecoration: 'none'
                    }}>
                      <Linkedin size={20} color="#FF6B6B" />
                      <span className="body-small">Connect on LinkedIn</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '40px', textAlign: 'center' }}>
              Frequently Asked <span style={{ color: '#00D4FF' }}>Questions</span>
            </h2>
            <div>
              {faqs.map((faq, index) => (
                <div key={index} style={{
                  marginBottom: '16px',
                  border: '1px solid var(--border-subtle)',
                  background: openFaq === index ? 'rgba(0, 212, 255, 0.05)' : 'transparent'
                }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    style={{
                      width: '100%',
                      padding: '20px 24px',
                      background: 'transparent',
                      border: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <span className="heading-3">{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} color="#00D4FF" /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div style={{ padding: '0 24px 20px' }}>
                      <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 1024px) {
          .form-layout {
            grid-template-columns: 1fr !important;
          }
          .side-panel {
            position: static !important;
            order: -1;
            margin-bottom: 40px;
          }
        }
        @media (max-width: 768px) {
          .form-row, .form-row-3, .checkbox-grid {
            grid-template-columns: 1fr !important;
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CafetrixSchedule;
